import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CarCard } from "../components/CarCard";
import { CompareBar } from "../components/CompareBar";
import { ReviewsSection } from "../components/ReviewsSection";
import { PwaPromoBanner } from "../components/PwaPromoBanner";
import { NotificationInlineCard } from "../components/NotificationInlineCard";
import { HeroSlideshow } from "../components/HeroSlideshow";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Search, SlidersHorizontal, X, ArrowRight, Crown, Shield, Handshake, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg";
const PAGE_SIZE = 8;

const Hero = () => (
  <section className="relative overflow-hidden bg-midnight min-h-[560px]" data-testid="hero-section">
    <HeroSlideshow fallback={HERO_IMG} />
    <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-20 md:pt-32 pb-24 md:pb-40">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-3 mb-8">
          <span className="divider-gold" />
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">
            Guzzardella Auto
          </span>
        </div>
        <h1 className="font-display text-white text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] mb-6">
          Auto Usate
          <span className="block text-gold italic mt-[28px] md:mt-[32px]">senza sorprese</span>
        </h1>
        <p className="text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed mb-10 font-light">
          Selezione curata di veicoli usati controllati uno ad uno. Ogni auto è personalmente
          scelta e verificata prima di entrare nel nostro parco.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="#inventario"
            className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-widest font-semibold"
            data-testid="hero-browse-btn"
          >
            Scopri il Parco Auto <ArrowRight size={16} />
          </a>
          <Link
            to="/richiedi"
            className="inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-widest font-semibold text-white border border-white/30 hover:border-gold hover:text-gold transition-colors"
            data-testid="hero-request-btn"
          >
            Cerchi un modello specifico?
          </Link>
        </div>
      </div>
    </div>

    <div className="relative bg-white/5 border-t border-white/10 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 grid grid-cols-3 gap-4 text-center">
        {[
          { icon: Crown, label: "Usato Selezionato", value: "100%" },
          { icon: Shield, label: "Garanzia Verificata", value: "12 mesi" },
          { icon: Handshake, label: "Trattative Trasparenti", value: "Sempre" },
        ].map((f) => (
          <div key={f.label} className="flex flex-col items-center gap-2">
            <f.icon size={18} className="text-gold" />
            <p className="font-display text-white text-xl md:text-2xl font-semibold">{f.value}</p>
            <p className="text-white/50 text-[10px] md:text-xs uppercase tracking-widest">{f.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FUEL_OPTIONS = ["Benzina", "Diesel", "GPL", "Metano", "Ibrida", "Elettrica"];
const TRANSMISSION_OPTIONS = ["Manuale", "Automatico"];
const BODY_TYPE_OPTIONS = ["City Car", "Berlina", "Station Wagon", "SUV", "Coupé", "Cabrio", "Monovolume", "Minibus"];

const Filters = ({ filters, setFilters, brands, onReset }) => {
  const updNum = (k) => (e) => {
    const v = e.target.value.replace(/[^\d]/g, "");
    setFilters({ ...filters, [k]: v === "" ? "" : parseInt(v, 10) });
  };
  return (
  <div className="card-sharp p-6 md:p-8 space-y-6 text-midnight" data-testid="filters-panel">
    <div className="flex items-center justify-between">
      <h3 className="font-display text-2xl font-semibold">Filtri</h3>
      <button
        onClick={onReset}
        className="text-xs uppercase tracking-widest text-midnight/50 hover:text-gold flex items-center gap-1"
        data-testid="filter-reset-btn"
      >
        <X size={12} /> Reset
      </button>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Cerca</label>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-midnight/40" />
        <Input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Marca, modello..."
          className="pl-9 rounded-sm border-border text-midnight placeholder:text-midnight/40"
          data-testid="filter-search-input"
        />
      </div>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Marca</label>
      <Select
        value={filters.brand}
        onValueChange={(v) => setFilters({ ...filters, brand: v })}
      >
        <SelectTrigger className="rounded-sm border-border text-midnight data-[placeholder]:text-midnight/50" data-testid="filter-brand-select">
          <SelectValue placeholder="Tutte le marche" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte le marche</SelectItem>
          {brands.map((b) => (
            <SelectItem key={b} value={b}>{b}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Tipologia</label>
      <Select
        value={filters.bodyType}
        onValueChange={(v) => setFilters({ ...filters, bodyType: v })}
      >
        <SelectTrigger className="rounded-sm border-border text-midnight data-[placeholder]:text-midnight/50" data-testid="filter-body-type-select">
          <SelectValue placeholder="Tutte" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte</SelectItem>
          {BODY_TYPE_OPTIONS.map((b) => (
            <SelectItem key={b} value={b}>{b}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Alimentazione</label>
      <Select
        value={filters.fuel}
        onValueChange={(v) => setFilters({ ...filters, fuel: v })}
      >
        <SelectTrigger className="rounded-sm border-border text-midnight data-[placeholder]:text-midnight/50" data-testid="filter-fuel-select">
          <SelectValue placeholder="Tutte" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte</SelectItem>
          {FUEL_OPTIONS.map((f) => (
            <SelectItem key={f} value={f}>{f}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Cambio</label>
      <Select
        value={filters.transmission}
        onValueChange={(v) => setFilters({ ...filters, transmission: v })}
      >
        <SelectTrigger className="rounded-sm border-border text-midnight data-[placeholder]:text-midnight/50" data-testid="filter-transmission-select">
          <SelectValue placeholder="Tutti" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {TRANSMISSION_OPTIONS.map((t) => (
            <SelectItem key={t} value={t}>{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Prezzo (€)</label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">da</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.minPrice ?? ""}
            onChange={updNum("minPrice")}
            placeholder="0"
            className="rounded-sm border-border"
            data-testid="filter-min-price-input"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">a</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.maxPrice ?? ""}
            onChange={updNum("maxPrice")}
            placeholder="Illimitato"
            className="rounded-sm border-border"
            data-testid="filter-max-price-input"
          />
        </div>
      </div>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Chilometri</label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">da</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.minKm ?? ""}
            onChange={updNum("minKm")}
            placeholder="0"
            className="rounded-sm border-border text-midnight placeholder:text-midnight/40"
            data-testid="filter-min-km-input"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">a</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.maxKm ?? ""}
            onChange={updNum("maxKm")}
            placeholder="Illimitato"
            className="rounded-sm border-border text-midnight placeholder:text-midnight/40"
            data-testid="filter-max-km-input"
          />
        </div>
      </div>
    </div>

    <div>
      <label className="text-xs uppercase tracking-widest text-midnight/60 mb-3 block">Anno</label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">da</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.minYear ?? ""}
            onChange={updNum("minYear")}
            placeholder="1990"
            className="rounded-sm border-border text-midnight placeholder:text-midnight/40"
            data-testid="filter-min-year-input"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-widest text-midnight/50 block mb-1">a</span>
          <Input
            type="text"
            inputMode="numeric"
            value={filters.maxYear ?? ""}
            onChange={updNum("maxYear")}
            placeholder={String(new Date().getFullYear())}
            className="rounded-sm border-border text-midnight placeholder:text-midnight/40"
            data-testid="filter-max-year-input"
          />
        </div>
      </div>
    </div>
  </div>
  );
};

export default function Home() {
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    brand: "all",
    bodyType: "all",
    fuel: "all",
    transmission: "all",
    minPrice: "",
    maxPrice: "",
    minKm: "",
    maxKm: "",
    minYear: "",
    maxYear: "",
  });

  const fetchCars = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.brand && filters.brand !== "all") params.brand = filters.brand;
      if (filters.bodyType && filters.bodyType !== "all") params.body_type = filters.bodyType;
      if (filters.fuel && filters.fuel !== "all") params.fuel = filters.fuel;
      if (filters.transmission && filters.transmission !== "all") params.transmission = filters.transmission;
      if (filters.minPrice !== "" && filters.minPrice != null) params.min_price = filters.minPrice;
      if (filters.maxPrice !== "" && filters.maxPrice != null) params.max_price = filters.maxPrice;
      if (filters.minKm !== "" && filters.minKm != null) params.min_km = filters.minKm;
      if (filters.maxKm !== "" && filters.maxKm != null) params.max_km = filters.maxKm;
      if (filters.minYear !== "" && filters.minYear != null) params.min_year = filters.minYear;
      if (filters.maxYear !== "" && filters.maxYear != null) params.max_year = filters.maxYear;
      const r = await api.get("/cars", { params });
      setCars(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const r = await api.get("/cars/brands");
      setBrands(r.data);
    } catch (e) {}
  };

  useEffect(() => {
    fetchBrands();
    // Anonymous private visit counter (visible only to admin)
    try {
      let sid = localStorage.getItem("guzz_sid");
      if (!sid) {
        sid = (crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random()}`);
        localStorage.setItem("guzz_sid", sid);
      }
      api.post("/analytics/visit", {
        path: window.location.pathname,
        session_id: sid,
        referer: document.referrer || null,
      }).catch(() => {});
    } catch (e) {}
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchCars, 300);
    return () => clearTimeout(t);
  }, [filters]);

  // Track searches for admin analytics widget (debounced)
  useEffect(() => {
    const hasSignal =
      (filters.search && filters.search.trim().length > 1) ||
      filters.brand !== "all" ||
      filters.bodyType !== "all" ||
      filters.fuel !== "all" ||
      filters.transmission !== "all" ||
      filters.minPrice !== "" || filters.maxPrice !== "";
    if (!hasSignal) return;
    const t = setTimeout(() => {
      try {
        const sid = localStorage.getItem("guzz_sid") || null;
        api.post("/analytics/search", {
          query: filters.search || "",
          brand: filters.brand !== "all" ? filters.brand : null,
          body_type: filters.bodyType !== "all" ? filters.bodyType : null,
          fuel: filters.fuel !== "all" ? filters.fuel : null,
          transmission: filters.transmission !== "all" ? filters.transmission : null,
          min_price: filters.minPrice !== "" ? parseFloat(filters.minPrice) : null,
          max_price: filters.maxPrice !== "" ? parseFloat(filters.maxPrice) : null,
          session_id: sid,
        }).catch(() => {});
      } catch (e) {}
    }, 1200);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(cars.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedCars = cars.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goToPage = (n) => {
    const clamped = Math.max(1, Math.min(totalPages, n));
    setPage(clamped);
    // Scroll to the top of the inventory section AFTER React re-renders.
    // A double rAF + fallback timeout makes this reliable on both desktop
    // and mobile (mobile Safari sometimes ignores the first scrollTo).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById("inventario");
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 20;
        window.scrollTo({ top: y, behavior: "smooth" });
        setTimeout(() => window.scrollTo({ top: y, behavior: "smooth" }), 250);
      });
    });
  };

  const onReset = () =>
    setFilters({
      search: "", brand: "all", bodyType: "all", fuel: "all", transmission: "all",
      minPrice: "", maxPrice: "", minKm: "", maxKm: "", minYear: "", maxYear: "",
    });

  return (
    <div className="App min-h-screen flex flex-col" data-testid="home-page">
      <Header />
      <Hero />
      <PwaPromoBanner />
      <NotificationInlineCard />

      {cars.some((c) => c.is_on_sale && !c.is_sold) && (
        <section id="promozioni" className="relative overflow-hidden bg-transparent border-y border-gold/30">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <div className="inline-flex items-center gap-3 mb-4">
                  <span className="divider-gold" />
                  <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">
                    Promo attive
                  </span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight animate-blink-gold" data-testid="promo-section-title">
                  Promozione
                </h2>
                <p className="text-midnight/70 mt-3 max-w-xl">
                  Occasioni selezionate personalmente da Rocco. Approfitta di prezzi ribassati per tempo limitato.
                </p>
              </div>
              <p className="text-sm text-midnight/60 shrink-0" data-testid="promo-count">
                {cars.filter((c) => c.is_on_sale && !c.is_sold).length} auto in offerta
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="promo-grid">
              {cars
                .filter((c) => c.is_on_sale && !c.is_sold)
                .map((c, i) => (
                  <CarCard key={c.id} car={c} index={i} />
                ))}
            </div>
          </div>
        </section>
      )}

      <section id="inventario" className="max-w-[1600px] mx-auto px-5 md:px-8 py-16 md:py-24 w-full">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">
                Parco Auto
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white">
              Auto disponibili
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-sm text-white/60" data-testid="cars-count">
              {loading ? "Caricamento..." : `${cars.length} auto disponibili`}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="md:hidden rounded-sm"
              onClick={() => setShowMobileFilters((s) => !s)}
              data-testid="mobile-filters-toggle"
            >
              <SlidersHorizontal size={14} className="mr-2" /> Filtri
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
          <aside className={`${showMobileFilters ? "block" : "hidden md:block"}`}>
            <div className="md:sticky md:top-24">
              <Filters filters={filters} setFilters={setFilters} brands={brands} onReset={onReset} />
            </div>
          </aside>

          <div className="min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="card-sharp overflow-hidden">
                    <div className="aspect-[4/3] bg-muted animate-pulse" />
                    <div className="p-7 space-y-3">
                      <div className="h-4 w-1/3 bg-muted animate-pulse" />
                      <div className="h-6 w-2/3 bg-muted animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : cars.length === 0 ? (
              <div className="card-sharp p-16 text-center">
                <Crown size={40} className="text-gold/40 mx-auto mb-4" />
                <h3 className="font-display text-2xl mb-3">Nessuna auto trovata</h3>
                <p className="text-midnight/60 mb-6">
                  Nessun veicolo corrisponde ai criteri di ricerca. Prova a modificare i filtri o richiedi un modello specifico.
                </p>
                <Link
                  to="/richiedi"
                  className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest font-semibold"
                  data-testid="empty-request-btn"
                >
                  Richiedi un'Auto <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10" data-testid="cars-grid">
                  {pagedCars.map((c, i) => (
                    <CarCard key={c.id} car={c} index={i} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav
                    className="mt-12 flex items-center justify-center gap-2"
                    aria-label="Paginazione parco auto"
                    data-testid="inventory-pagination"
                  >
                    {currentPage > 1 && (
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        className="h-10 px-4 flex items-center gap-2 text-xs uppercase tracking-widest border border-border hover:border-gold hover:text-gold transition-colors"
                        data-testid="pagination-prev"
                      >
                        <ChevronLeft size={14} /> Precedente
                      </button>
                    )}
                    <div className="flex items-center gap-1 mx-2">
                      {Array.from({ length: totalPages }).map((_, i) => {
                        const n = i + 1;
                        const active = n === currentPage;
                        return (
                          <button
                            key={n}
                            onClick={() => goToPage(n)}
                            aria-current={active ? "page" : undefined}
                            className={`h-10 w-10 text-sm font-bold text-gold transition-colors ${
                              active
                                ? "bg-midnight border border-gold shadow-[0_0_12px_rgba(212,175,55,0.4)]"
                                : "border border-gold/40 hover:bg-gold/10 hover:border-gold"
                            }`}
                            data-testid={`pagination-page-${n}`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`h-10 px-4 flex items-center gap-2 text-xs uppercase tracking-widest border border-border hover:border-gold hover:text-gold transition-colors ${currentPage === totalPages ? "hidden" : ""}`}
                      data-testid="pagination-next"
                    >
                      Successiva <ChevronRight size={14} />
                    </button>
                  </nav>
                )}

                <p className="mt-6 text-center text-xs font-semibold uppercase tracking-widest text-gold" data-testid="pagination-info">
                  Pagina {currentPage} di {totalPages} · {cars.length} auto in totale
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <ReviewsSection />

      <Footer />
      <CompareBar />
    </div>
  );
}
